<template>
  <div class="login-page" :class="{ compact: embedded }">
    <div class="login-card card">
      <div class="login-logo">
        <img src="/logo.png" alt="SalesScout" class="login-logo-img" />
      </div>
      <h1 class="login-title">{{ portalName }}</h1>
      <p class="login-subtitle">Sign in to {{ getConfig()?.companyName || portalName }}</p>
      <button v-if="!embedded" type="button" class="btn btn-ghost" @click="changeCompany">Change company</button>

      <p v-if="embedded">Your unfinished work is still open. After verification, review it and press Save to finish.</p>
      <p v-if="authStore.notice" role="status">{{ authStore.notice }}</p>
      <button v-if="authStore.canRetry" type="button" class="btn btn-secondary" :disabled="authStore.isLoading" @click="retryRemembered">Retry sign-in</button>
      <button v-if="!embedded && authStore.remembered" type="button" class="btn btn-ghost" @click="forgetDevice">Forget this device</button>

      <!-- License limit alert (takes priority over generic error) -->
      <div v-if="authStore.licenseLimitInfo" class="license-limit-alert">
        <div class="license-limit-heading">License limit reached</div>
        <template v-if="authStore.licenseLimitInfo.userHasExistingSession">
          <p class="license-limit-text">
            All {{ authStore.licenseLimitInfo.maxUsers ?? 'available' }} license seats are currently in use.
            You have {{ authStore.licenseLimitInfo.userExistingSessionCount }}
            active session{{ authStore.licenseLimitInfo.userExistingSessionCount === 1 ? '' : 's' }} elsewhere.
          </p>
          <p class="license-limit-text">
            Would you like to end your oldest session (session {{ authStore.licenseLimitInfo.replaceSessionId }}) and sign in here?
          </p>
          <button
            class="btn btn-primary btn-sm license-limit-btn"
            :disabled="authStore.isLoading"
            @click="forceLogin"
          >
            <span v-if="authStore.isLoading" class="spinner" style="width: 0.875rem; height: 0.875rem;" />
            <span v-else>End oldest session and sign in</span>
          </button>
        </template>
        <p v-else class="license-limit-text">
          All {{ authStore.licenseLimitInfo.maxUsers ?? 'available' }} license seats are currently in use.
          Please try again later or ask another user to sign out.
        </p>
      </div>

      <!-- Generic error alert -->
      <div v-else-if="authStore.error" class="alert alert-error" role="alert" style="margin-bottom: 0.5rem;">
        {{ authStore.error }}
      </div>

      <!-- ── Step 1: Email entry ──────────────────────────────────────────── -->
      <form v-if="step === 'email'" @submit.prevent="submitEmail" class="login-form">
        <div class="form-group">
          <label for="email">Email address</label>
          <input
            id="email"
            v-model="email"
            type="email"
            placeholder="you@yourcompany.com"
            autocomplete="email"
            aria-describedby="email-instructions"
            required
            :readonly="embedded"
            :disabled="authStore.isLoading"
          />
          <span id="email-instructions" class="form-hint">
            Enter the email address set up for you in AccountScout. We’ll email you a sign-in code.
          </span>
          <details ref="emailHelp" class="email-help">
            <summary>Which email should I use?</summary>
            <p>SalesScout uses the <strong>Private Email</strong> listed for your user under <strong>AccountScout → Define → Users</strong>.</p>
            <p>If you can’t find it or don’t have access to that screen, contact Fake Brains Support below.</p>
          </details>
        </div>

        <div ref="turnstileRef" class="turnstile-container"></div>

        <div v-if="captchaError" class="alert alert-error captcha-error">
          {{ captchaError }}
        </div>

        <button v-if="!showDemoLogin" type="submit" class="btn btn-primary btn-block btn-lg" :disabled="authStore.isLoading || !email || !captchaToken">
          <span v-if="authStore.isLoading" class="spinner" />
          <span v-else>Send verification code</span>
        </button>

        <button
          v-if="showDevLogin && !showDemoLogin"
          type="button"
          class="btn btn-secondary btn-block btn-lg dev-login-btn"
          :disabled="authStore.isLoading || !email"
          @click="submitDevLogin"
        >
          <span v-if="authStore.isLoading" class="spinner" />
          <span v-else>Local Dev Sign In</span>
        </button>

        <div v-if="showDevLogin && !showDemoLogin" class="dev-login-hint">
          Uses the localhost-only dev login path and skips email verification.
        </div>
      </form>

      <!-- ── Demo login section ──────────────────────────────────────────── -->
      <div v-if="showDemoLogin && step === 'email'" class="demo-section">
        <div class="demo-divider">
          <span>Internal demo access</span>
        </div>
        <form @submit.prevent="submitDemoLogin" class="login-form">
          <div class="form-group">
            <label for="demoSecret">Access key</label>
            <input
              id="demoSecret"
              v-model="demoSecret"
              type="password"
              placeholder="Enter access key"
              required
              :disabled="authStore.isLoading"
            />
          </div>
          <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="authStore.isLoading || !email || !demoSecret">
            <span v-if="authStore.isLoading" class="spinner" />
            <span v-else>Demo Sign In</span>
          </button>
        </form>
      </div>

      <!-- ── Step 2: Code entry ───────────────────────────────────────────── -->
      <form v-else-if="step === 'code'" @submit.prevent="submitCode" class="login-form">
        <p class="code-hint">
          A 6-digit verification code was sent to <strong>{{ email }}</strong>.
          Check your inbox and enter the code below.
        </p>

        <div class="form-group">
          <label for="code">Verification code</label>
          <input
            id="code"
            v-model="code"
            type="text"
            inputmode="numeric"
            maxlength="6"
            placeholder="000000"
            autocomplete="one-time-code"
            required
            :disabled="authStore.isLoading"
            class="code-input"
          />
        </div>

        <label v-if="authStore.rememberAvailable" class="remember-choice">
          <input v-model="rememberDevice" type="checkbox" :disabled="authStore.isLoading" />
          <span><strong>Remember this device</strong><br />For personal devices. Stay signed in unless you're away for seven days. Verify your email again at least every 30 days.</span>
        </label>
        <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="authStore.isLoading || code.length < 6">
          <span v-if="authStore.isLoading" class="spinner" />
          <span v-else>Verify code</span>
        </button>

        <div class="resend-row">
          <button
            type="button"
            class="btn btn-ghost btn-sm"
            :disabled="resendCooldown > 0 || authStore.isLoading"
            @click="resendCode"
          >
            {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code' }}
          </button>
          <button type="button" class="btn btn-ghost btn-sm" @click="goBack">
            {{ embedded ? 'Request a new code' : 'Change email' }}
          </button>
        </div>
      </form>
      <SignInSupport />
    </div>
  </div>
</template>

<script setup lang="ts">
import { companyPath, safeCompanyReturn, changeCompany } from '@/utils/companyNavigation'
import { nextTick, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getConfig, isDemoEnabled, isDevelopment } from '@/api/config'
import SignInSupport from '@/components/SignInSupport.vue'

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

const props = defineProps<{ embedded?: boolean }>()
const emit = defineEmits<{ authenticated: [] }>()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const portalName = computed(() => getConfig()?.portalName ?? 'SalesScout')
const showDemoLogin = computed(() => !props.embedded && route.query.mode === 'demo' && isDemoEnabled())
const showDevLogin = computed(() => !props.embedded && isDevelopment())

type Step = 'email' | 'code'
const step = ref<Step>('email')
const email = ref(props.embedded ? authStore.repEmail : '')
const rememberDevice = ref(props.embedded && authStore.remembered)
const emailHelp = ref<HTMLDetailsElement | null>(null)
const code = ref('')
const demoSecret = ref('')
const resendCooldown = ref(0)
let cooldownInterval: ReturnType<typeof setInterval> | null = null

// Cloudflare Turnstile
const siteKey = __TURNSTILE_SITE_KEY__
const turnstileRef = ref<HTMLElement | null>(null)
const captchaToken = ref<string | null>(null)
const captchaError = ref<string | null>(null)
let widgetId: string | null = null

function renderTurnstile() {
  if (!window.turnstile || !turnstileRef.value) return
  captchaError.value = null
  if (widgetId) {
    window.turnstile.remove(widgetId)
    widgetId = null
  }
  widgetId = window.turnstile.render(turnstileRef.value, {
    sitekey: siteKey,
    callback: (token: string) => {
      captchaToken.value = token
      captchaError.value = null
    },
    'expired-callback': () => { captchaToken.value = null },
    'error-callback': () => {
      captchaToken.value = null
      captchaError.value = 'Verification could not load. Refresh the page and make sure challenges.cloudflare.com is not blocked.'
    },
    theme: 'auto'
  })
}

function resetTurnstile() {
  captchaToken.value = null
  if (widgetId && window.turnstile) {
    window.turnstile.reset(widgetId)
  }
}

onMounted(() => {
  // Poll for turnstile script to load (up to 10 seconds)
  let attempts = 0
  const interval = setInterval(() => {
    attempts++
    if (window.turnstile) {
      clearInterval(interval)
      renderTurnstile()
    } else if (attempts > 100) {
      clearInterval(interval)
      captchaError.value = 'Verification could not load. Refresh the page and make sure challenges.cloudflare.com is not blocked.'
    }
  }, 100)
})

function startCooldown() {
  resendCooldown.value = 60
  cooldownInterval = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0 && cooldownInterval) {
      clearInterval(cooldownInterval)
      cooldownInterval = null
    }
  }, 1000)
}

async function submitEmail() {
  authStore.clearError()
  const result = await authStore.sendVerificationCode(email.value, captchaToken.value || undefined)
  resetTurnstile()
  if (result.errorCode === 'email_not_recognized' && emailHelp.value) emailHelp.value.open = true
  if (result.success && result.emailSent) {
    step.value = 'code'
    code.value = ''
    startCooldown()
  }
}

async function submitDemoLogin() {
  authStore.clearError()
  const ok = await authStore.demoSignIn(email.value, demoSecret.value)
  if (ok) {
    completed()
  }
}

async function submitDevLogin() {
  authStore.clearError()
  const ok = await authStore.tryDevAutoLogin(email.value)
  if (ok) {
    completed()
  }
}

async function submitCode() {
  authStore.clearError()
  const ok = await authStore.verifyCode(email.value, code.value, false, rememberDevice.value)
  if (ok) {
    completed()
  }
}

async function forceLogin() {
  const ok = await authStore.verifyCode(email.value, code.value, true, rememberDevice.value)
  if (ok) {
    completed()
  }
}

/** Completes verification in place without unmounting the unfinished activity. */
function completed() {
  if (props.embedded) emit('authenticated')
  else router.replace(safeCompanyReturn(route.query.returnTo) || companyPath('/dashboard'))
}

async function retryRemembered() {
  if (await authStore.resume(true)) completed()
}

async function forgetDevice() {
  if (window.confirm('Forget this browser for this company? Your next sign-in will require an email code.')) await authStore.forgetDevice()
}

function resendCode() {
  // A new code requires a fresh, single-use CAPTCHA token.
  goBack()
}

function goBack() {
  step.value = 'email'
  code.value = ''
  authStore.clearError()
  if (cooldownInterval) {
    clearInterval(cooldownInterval)
    cooldownInterval = null
  }
  resendCooldown.value = 0
  nextTick(renderTurnstile)
}

onUnmounted(() => {
  if (cooldownInterval) clearInterval(cooldownInterval)
  if (widgetId && window.turnstile) {
    window.turnstile.remove(widgetId)
    widgetId = null
  }
})
</script>

<style scoped>
.login-page.compact { min-height: auto; padding: 0; }
.remember-choice { display: flex; align-items: flex-start; gap: .75rem; font-size: .875rem; line-height: 1.5; }
.login-form .remember-choice input { flex: 0 0 auto; width: 20px; min-height: 20px; margin-top: .25rem; }
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: var(--bg-primary);
}

.login-card {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.login-logo {
  display: flex;
  justify-content: center;
}
.login-logo-img {
  height: 48px;
  width: auto;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  color: var(--text-primary);
}

.login-subtitle {
  font-size: 0.9375rem;
  text-align: center;
  color: var(--text-secondary);
  margin-top: -0.75rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.email-help { color: var(--text-secondary); font-size: .875rem; line-height: 1.5; }
.email-help summary { color: var(--accent-primary); cursor: pointer; padding-block: .5rem; }
.email-help summary:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; border-radius: 2px; }
.email-help p { margin: .5rem 0; }
.login-form input { min-height: 48px; font-size: 1rem; }

.code-hint {
  font-size: 0.9375rem;
  color: var(--text-secondary);
  line-height: 1.5;
}

.code-input {
  font-size: 1.5rem !important;
  letter-spacing: 0.4em;
  text-align: center;
  font-weight: 600;
}

.resend-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -0.25rem;
}

/* License limit alert */
.license-limit-alert {
  background-color: var(--danger-50, #fef2f2);
  border: 1px solid var(--danger-200, #fecaca);
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  margin-bottom: 0.5rem;
}
.license-limit-heading {
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--danger-700, #b91c1c);
  margin-bottom: 0.375rem;
}
.license-limit-text {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--danger-700, #b91c1c);
  margin: 0 0 0.375rem 0;
}
.license-limit-text:last-of-type {
  margin-bottom: 0;
}
.license-limit-btn {
  margin-top: 0.5rem;
  width: 100%;
}

.dev-login-btn {
  margin-top: -0.25rem;
}

.dev-login-hint {
  margin-top: -0.5rem;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--text-muted);
  text-align: center;
}

/* Turnstile CAPTCHA */
.turnstile-container {
  display: flex;
  justify-content: center;
}

.captcha-error {
  margin-bottom: 0;
}

/* Demo login section */
.demo-section {
  margin-top: 0.25rem;
}
.demo-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: var(--text-tertiary, #9ca3af);
  font-size: 0.8125rem;
}
.demo-divider::before,
.demo-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--border-secondary, #e5e7eb);
}
</style>
